// Emergency Numbers → Alerts. The numbers a 911 call would fail for, and the digest that reports them.
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { BellRing, Loader2, Send, Settings2 } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { NumberStatusPill } from "./Numbers";
import { Empty, Pill, SIDE, ago, fmtTn } from "./ui";

export default function Alerts({ canEdit, refreshKey, onOpenDomain }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data: d } = await api.get("/e911/alerts");
      setData(d);
    } catch {
      toast.error("Could not load the alerts");
    }
  }, []);

  useEffect(() => { load(); }, [load, refreshKey]);

  const send = async () => {
    setBusy(true);
    try {
      const { data: r } = await api.post("/e911/alerts/send");
      if (r.status === "skipped") toast.warning(`Not sent — ${r.reason}`);
      else toast.success(`Digest ${r.status === "paused" ? "held by the email pause" : "sent"} · ${r.count} number(s), ${r.domains ?? 0} domain(s) to ${(r.recipients || []).join(", ")}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send the digest");
    } finally {
      setBusy(false);
    }
  };

  if (!data) return <Loader2 className="h-5 w-5 animate-spin text-slate-300" />;
  const rows = data.items || [];
  const domains = data.address_domains || [];
  const recipients = data.recipients || [];
  const last = data.last_digest;
  const DEFAULT = { ok: ["set", "text-slate-500"], none: ["not set", "font-semibold text-rose-700"], unknown: ["unknown id", "font-semibold text-amber-700"] };

  return (
    <div className="space-y-4" data-testid="e911-alerts">
      <div className="flex flex-wrap items-center gap-2">
        <p className="max-w-3xl text-sm text-slate-500" data-testid="e911-alerts-rule">
          {data.enabled === false
            ? "The daily digest is switched off in Settings → Emergency Numbers; this list is in-app only."
            : <>Every day at <strong>07:00</strong> the numbers and domains below go to{" "}
              {recipients.length ? <strong>{recipients.join(", ")}</strong> : <strong className="text-amber-700">nobody — add recipients in Settings</strong>}.
              {last ? <> Last digest {ago(last.at)} ({last.status}, {last.count} number{last.count === 1 ? "" : "s"}{last.domains != null ? `, ${last.domains} domain${last.domains === 1 ? "" : "s"}` : ""}).</> : " No digest has gone out yet."}</>}
        </p>
        <div className="ml-auto flex items-center gap-2">
          {canEdit && (
            <Button size="sm" onClick={send} disabled={busy || !recipients.length} data-testid="e911-alerts-send"
                    title={recipients.length ? "" : "Add recipients in Settings first"}>
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send the digest now
            </Button>
          )}
          {canEdit && (
            <Link to="/settings?tab=e911" data-testid="e911-alerts-settings-link">
              <Button size="sm" variant="outline"><Settings2 className="h-4 w-4" /> Recipients</Button>
            </Link>
          )}
        </div>
      </div>

      <div className="flex flex-wrap gap-2 text-sm" data-testid="e911-alerts-counts">
        <Pill tone="bg-rose-100 text-rose-800">{rows.filter((r) => r.status === "not_registered").length} not registered</Pill>
        <Pill tone="bg-amber-100 text-amber-800">{rows.filter((r) => r.status === "wrong").length} wrong side</Pill>
        <Pill tone="bg-orange-100 text-orange-800" testid="e911-alerts-address-count">{data.address_total ?? domains.length} domain{(data.address_total ?? domains.length) === 1 ? "" : "s"} missing an address</Pill>
        {data.total > rows.length && <span className="text-xs text-slate-500">showing {rows.length} of {data.total}</span>}
      </div>

      <Card className="overflow-x-auto">
        <table className="w-full text-left text-sm">
          <thead className="text-[11px] uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-3 py-1.5">Status</th>
              <th className="px-3 py-1.5">Number</th>
              <th className="px-3 py-1.5">Domain</th>
              <th className="px-3 py-1.5">Territory</th>
              <th className="px-3 py-1.5">Portal side</th>
              <th className="px-3 py-1.5">Bandwidth side</th>
              <th className="px-3 py-1.5">Users</th>
              <th className="px-3 py-1.5">Address on file</th>
            </tr>
          </thead>
          <tbody data-testid="e911-alerts-body">
            {rows.map((r) => (
              <tr key={`${r.platform}/${r.domain}/${r.number}`} className="border-t border-slate-100">
                <td className="px-3 py-1.5"><NumberStatusPill status={r.status} /></td>
                <td className="px-3 py-1.5 font-mono text-[12px] text-slate-800">{fmtTn(r.number)}</td>
                <td className="px-3 py-1.5">
                  <button type="button" className="font-medium text-slate-800 hover:text-primary hover:underline"
                          onClick={() => onOpenDomain({ platform: r.platform, domain: r.domain })}
                          data-testid={`e911-alert-domain-${r.number}`}>{r.domain}</button>
                </td>
                <td className="px-3 py-1.5 text-slate-500">{r.territory || "—"}</td>
                <td className="px-3 py-1.5 text-slate-700">{SIDE[r.portal_type] || r.portal_type}</td>
                <td className="px-3 py-1.5 text-slate-700">{SIDE[r.bandwidth_type] || r.bandwidth_type}</td>
                <td className="px-3 py-1.5 text-slate-600" title={(r.users || []).join(", ")}>{r.user_count || 0}</td>
                <td className="px-3 py-1.5 text-[12px] text-slate-500">{r.address || "—"}</td>
              </tr>
            ))}
            {!rows.length && (
              <Empty colSpan={8} testid="e911-alerts-empty">
                <BellRing className="mx-auto mb-2 h-5 w-5 text-emerald-500" />
                Nothing to alert on — every number in the report is registered where it should be, or the report has not been built yet.
              </Empty>
            )}
          </tbody>
        </table>
      </Card>

      <div>
        <h3 className="text-sm font-semibold text-slate-800" data-testid="e911-alerts-domains-title">
          Domains whose subscribers lack a usable emergency address
        </h3>
        <p className="mt-0.5 text-xs text-slate-500">
          DLR domains where a real user has no address of their own and no domain default to fall back on, points at an
          address-id the domain does not hold, or at an address different from what their Endpoint carries. Included in the same 07:00 digest.
        </p>
      </div>
      <Card className="overflow-x-auto">
        <table className="w-full text-left text-sm">
          <thead className="text-[11px] uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-3 py-1.5">Domain</th>
              <th className="px-3 py-1.5">Territory</th>
              <th className="px-3 py-1.5">Real users</th>
              <th className="px-3 py-1.5">No address</th>
              <th className="px-3 py-1.5">Unknown id</th>
              <th className="px-3 py-1.5" title="Own address differs from the one the Endpoint carries">≠ Endpoint</th>
              <th className="px-3 py-1.5">Number not an Endpoint</th>
              <th className="px-3 py-1.5">Domain default</th>
            </tr>
          </thead>
          <tbody data-testid="e911-alerts-domains-body">
            {domains.map((m) => {
              const [label, tone] = DEFAULT[m.default_state] || [m.default_state || "—", "text-slate-400"];
              return (
                <tr key={`${m.platform}/${m.domain}`} className="border-t border-slate-100" data-testid={`e911-alert-address-row-${m.domain}`}>
                  <td className="px-3 py-1.5">
                    <button type="button" className="font-medium text-slate-800 hover:text-primary hover:underline"
                            onClick={() => onOpenDomain({ platform: m.platform, domain: m.domain })}
                            data-testid={`e911-alert-address-domain-${m.domain}`}>{m.domain}</button>
                  </td>
                  <td className="px-3 py-1.5 text-slate-500">{m.territory || "—"}</td>
                  <td className="px-3 py-1.5 text-slate-700">{m.regular}</td>
                  <td className={`px-3 py-1.5 ${m.no_address ? "font-semibold text-rose-700" : "text-slate-300"}`}>{m.no_address || "—"}</td>
                  <td className={`px-3 py-1.5 ${m.unknown_address ? "font-semibold text-amber-700" : "text-slate-300"}`}>{m.unknown_address || "—"}</td>
                  <td className={`px-3 py-1.5 ${m.mismatch ? "font-semibold text-amber-700" : "text-slate-300"}`}>{m.mismatch || "—"}</td>
                  <td className={`px-3 py-1.5 ${m.bad_number ? "font-semibold text-rose-700" : "text-slate-300"}`}>{m.bad_number || "—"}</td>
                  <td className={`px-3 py-1.5 text-[12px] ${tone}`} title={m.default_address_id || ""}>{label}</td>
                </tr>
              );
            })}
            {!domains.length && (
              <Empty colSpan={8} testid="e911-alerts-domains-empty">
                <BellRing className="mx-auto mb-2 h-5 w-5 text-emerald-500" />
                Every assessed DLR domain resolves its real users to a registered address.
              </Empty>
            )}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
